// Cave84 — Inventory hook
// Joins Bottle + Vintage + Wine + Producer + Cellar + Appellation into rows.
// Provides both individual InventoryRow (backward compat) and GroupedInventoryRow
// (for the Inventory table view, which groups by vintage+cellar+format).
//
// Critical spec rule: "Available at Home" filter is ON by default.

import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { collection, getDocs, query, where } from 'firebase/firestore';
import type {
  Appellation,
  Bottle,
  BottleFormat,
  BottleStatus,
  Cellar,
  CriticScore,
  MarketPriceHistory,
  Producer,
  Vintage,
  Wine,
  WineStyle,
} from '@cave84/shared-types';
import { listAllBottles } from '@/services/bottles';
import { listAllVintages } from '@/services/vintages';
import { db } from '@/services/firebase';
import { getLatestPricesForVintages } from '@/services/marketPrices';
import { useWines } from '@/hooks/useWines';
import { useProducersMap } from '@/hooks/useProducers';
import { foldSearch } from '@/utils/searchText';
import { useCellars } from '@/hooks/useCellars';
import { useAppellationsMap } from '@/hooks/useAppellations';
import { accumulateValuation, emptyValuationTotals } from '@/utils/valuation';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type DrinkingReadiness =
  | 'too_young'
  | 'developing'
  | 'peak'
  | 'declining'
  | 'over_the_hill';

// ---------------------------------------------------------------------------
// InventoryRow - individual bottle (backward compat)
// ---------------------------------------------------------------------------

export interface InventoryRow {
  bottle: Bottle;
  vintage: Vintage;
  wine: Wine;
  producer: Producer;
  cellar: Cellar;
  drinkingStatus: DrinkingReadiness;
  /** Latest market price for this bottle's vintage — null when never priced. */
  marketPrice: number | null;
}

// ---------------------------------------------------------------------------
// GroupedInventoryRow - grouped by vintage + cellar + format
// ---------------------------------------------------------------------------

export interface GroupedInventoryRow {
  /** Composite key: vintage_id__cellar_id__format */
  key: string;
  wine: Wine;
  vintage: Vintage;
  producer: Producer;
  cellar: Cellar;
  appellation: Appellation | null;
  format: BottleFormat;
  qty: number;
  bottles: Bottle[];
  drinkingStatus: DrinkingReadiness;
  drinkingWindow: { from: number; to: number };
  region: string;
  isSpecialOccasion: boolean;
  avgScore: number | null;
  marketPrice: number | null;
  avgCostEur: number | null;
}

// ---------------------------------------------------------------------------
// InventoryFilters
// ---------------------------------------------------------------------------

export interface InventoryFilters {
  availableAtHome: boolean;
  cellarIds: string[];
  regions: string[];
  styles: WineStyle[];
  drinkingStatuses: DrinkingReadiness[];
  vintageFrom: number | null;
  vintageTo: number | null;
  specialOccasion: boolean;
  search: string;
  sortBy: 'drinking_window' | 'producer' | 'vintage_asc' | 'vintage_desc' | 'price_asc' | 'price_desc';
  // Legacy single-select fields (kept for backward compat)
  style: WineStyle | null;
  drinkingStatus: DrinkingReadiness | null;
}

export const DEFAULT_FILTERS: InventoryFilters = {
  availableAtHome: false,
  cellarIds: [],
  regions: [],
  styles: [],
  drinkingStatuses: [],
  vintageFrom: null,
  vintageTo: null,
  specialOccasion: false,
  search: '',
  sortBy: 'drinking_window',
  style: null,
  drinkingStatus: null,
};

// ---------------------------------------------------------------------------
// FacetCounts
// ---------------------------------------------------------------------------

export interface FacetCounts {
  byCellar: Map<string, number>;
  byRegion: Map<string, number>;
  byStyle: Map<WineStyle, number>;
  byDrinkingStatus: Map<DrinkingReadiness, number>;
  specialOccasionCount: number;
  totalBottles: number;
  homeBottles: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function computeDrinkingReadiness(vintage: Vintage): DrinkingReadiness {
  const now = new Date().getFullYear();
  const w = vintage.drinking_window_current;
  if (!w) return 'too_young';
  if (now < w.from_year) return 'too_young';
  if (now < w.peak_from) return 'developing';
  if (now <= w.peak_to)  return 'peak';
  if (now <= w.to_year)  return 'declining';
  return 'over_the_hill';
}

// ---------------------------------------------------------------------------
// Maturity distribution (WI-5)
// Collapses the 5-state DrinkingReadiness enum into the 4 bands the dashboard
// card shows: Too Young / In Window / At Peak / Past Peak. "In Window" covers
// both sides of peak (developing + declining) — i.e. inside the drinking
// window but not at its peak.
// ---------------------------------------------------------------------------

export type MaturityBand = 'too_young' | 'in_window' | 'at_peak' | 'past_peak';

/** Which DrinkingReadiness values collapse into each maturity band. */
export const MATURITY_BAND_STATUSES: Record<MaturityBand, DrinkingReadiness[]> = {
  too_young: ['too_young'],
  in_window: ['developing', 'declining'],
  at_peak:   ['peak'],
  past_peak: ['over_the_hill'],
};

export function maturityBandFor(status: DrinkingReadiness): MaturityBand {
  switch (status) {
    case 'too_young':     return 'too_young';
    case 'peak':           return 'at_peak';
    case 'over_the_hill':  return 'past_peak';
    case 'developing':
    case 'declining':
    default:                return 'in_window';
  }
}

// ---------------------------------------------------------------------------
// Drink Soon aggregation
// GroupedInventoryRow splits by vintage + cellar + format. Dashboard/Drink-Soon
// widgets want one row per producer+cuvée+vintage (vintage.id is already
// unique per wine+year, so it doubles as that composite key) with a summed
// qty across cellars/formats, plus a per-wine urgency metric instead of a
// single uniform status badge.
// ---------------------------------------------------------------------------

export interface DrinkSoonRow {
  /** vintage.id — unique per producer+cuvée+vintage, used to collapse cellar/format splits. */
  key: string;
  wine: Wine;
  vintage: Vintage;
  producer: Producer;
  qty: number;
  drinkingStatus: DrinkingReadiness;
  drinkingWindow: { from: number; peakFrom: number; peakTo: number; to: number };
}

export function aggregateDrinkSoonRows(rows: GroupedInventoryRow[]): DrinkSoonRow[] {
  const map = new Map<string, DrinkSoonRow>();
  for (const r of rows) {
    const existing = map.get(r.vintage.id);
    if (existing) {
      existing.qty += r.qty;
      continue;
    }
    const w = r.vintage.drinking_window_current;
    map.set(r.vintage.id, {
      key: r.vintage.id,
      wine: r.wine,
      vintage: r.vintage,
      producer: r.producer,
      qty: r.qty,
      drinkingStatus: r.drinkingStatus,
      drinkingWindow: {
        from: w?.from_year ?? 0,
        peakFrom: w?.peak_from ?? 0,
        peakTo: w?.peak_to ?? 0,
        to: w?.to_year ?? 0,
      },
    });
  }
  return Array.from(map.values());
}

/** Years left before the drinking window closes. Smaller = more urgent. Can go negative for over_the_hill. */
export function yearsUntilWindowCloses(window: { to: number }): number {
  return window.to - new Date().getFullYear();
}

/** Per-wine urgency label for the "declining" bucket — replaces a uniform "declining" badge. */
export function drinkSoonUrgencyLabel(window: { to: number }): string {
  const yearsLeft = yearsUntilWindowCloses(window);
  if (yearsLeft <= 0) return 'drink now';
  if (yearsLeft === 1) return 'drink within 1y';
  return `drink within ${yearsLeft}y`;
}

export function canonicalRegion(raw: string): string {
  const r = (raw ?? '').toLowerCase();
  if (r.includes('burgundy') || r.includes('bourgogne'))                    return 'Burgundy';
  if (r.includes('bordeaux'))                                                return 'Bordeaux';
  if (r.includes('champagne'))                                               return 'Champagne';
  if (r.includes('rh') && (r.includes('ne') || r.includes('ône')))          return 'Rhône';
  if (r.includes('italy') || r.includes('itali') || r.includes('piedmont') ||
      r.includes('tuscany') || r.includes('barolo') || r.includes('veneto') ||
      r.includes('sicily'))                                                   return 'Italy';
  if (r.includes('spain') || r.includes('rioja') || r.includes('ribera') ||
      r.includes('priorat') || r.includes('catalun') || r.includes('castilla') ||
      r.includes('jerez') || r.includes('rias baixas') || r.includes('rueda'))  return 'Spain';
  if (r.includes('germany') || r.includes('mosel') || r.includes('nahe') ||
      r.includes('rheingau') || r.includes('pfalz') || r.includes('franken') ||
      r.includes('rheinhessen'))                                              return 'Germany';
  if (r.includes('hungary') || r.includes('tokaj'))                          return 'Hungary';
  if (r.includes('alsace'))                                                   return 'Alsace';
  if (r.includes('loire'))                                                    return 'Loire';
  if (r.includes('portugal') || r.includes('douro') || r.includes('alentejo') ||
      r.includes('vinho verde') || r.includes('dao'))                         return 'Portugal';
  if (r.includes('california') || r.includes('napa') || r.includes('sonoma') ||
      r.includes('oregon') || r.includes('washington'))                       return 'USA';
  if (r.includes('argentina') || r.includes('mendoza'))                      return 'Argentina';
  if (r.includes('australia') || r.includes('barossa') || r.includes('mclaren') ||
      r.includes('margaret river') || r.includes('yarra'))                   return 'Australia';
  return 'Other';
}

// ---------------------------------------------------------------------------
// Raw data loaders
// ---------------------------------------------------------------------------

function useAllBottles(status: BottleStatus = 'in_cellar'): UseQueryResult<Bottle[]> {
  return useQuery({
    queryKey: ['bottles', 'all', status],
    queryFn: () => listAllBottles(status),
    staleTime: 60_000,
  });
}

function useAllVintages(): UseQueryResult<Vintage[]> {
  return useQuery({
    queryKey: ['vintages', 'all'],
    queryFn: listAllVintages,
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// useGroupedInventory - primary hook for the Inventory table
// ---------------------------------------------------------------------------

export function useGroupedInventory(filters: InventoryFilters = DEFAULT_FILTERS) {
  const { data: bottles = [], isLoading: loadingBottles } = useAllBottles('in_cellar');
  const { data: vintages = [], isLoading: loadingVintages } = useAllVintages();
  const { data: wines = [], isLoading: loadingWines } = useWines({});
  const producersMap = useProducersMap();
  const { data: cellars = [], isLoading: loadingCellars } = useCellars();
  const appellationsMap = useAppellationsMap();

  const isLoading = loadingBottles || loadingVintages || loadingWines || loadingCellars;

  const vintageMap = useMemo(() => new Map(vintages.map(v => [v.id, v])), [vintages]);
  const wineMap    = useMemo(() => new Map(wines.map(w => [w.id, w])),    [wines]);
  const cellarMap  = useMemo(() => new Map(cellars.map(c => [c.id, c])),  [cellars]);

  const homeCellarIds = useMemo(
    () => new Set(cellars.filter(c => c.is_home).map(c => c.id)),
    [cellars],
  );

  // Step 1: build all GroupedInventoryRow (unfiltered) for facet counts
  const allGrouped = useMemo<GroupedInventoryRow[]>(() => {
    const groups = new Map<string, GroupedInventoryRow>();
    for (const bottle of bottles) {
      const vintage = vintageMap.get(bottle.vintage_id);
      if (!vintage) continue;
      const wine = wineMap.get(vintage.wine_id);
      if (!wine) continue;
      const producer = producersMap.get(wine.producer_id);
      if (!producer) continue;
      const cellar = cellarMap.get(bottle.current_cellar_id);
      if (!cellar) continue;

      const key = `${vintage.id}__${cellar.id}__${bottle.format}`;
      const existing = groups.get(key);
      if (existing) {
        existing.qty += 1;
        existing.bottles.push(bottle);
        if (bottle.is_special_occasion) existing.isSpecialOccasion = true;
      } else {
        const appellation = appellationsMap.get(wine.appellation_id) ?? null;
        const drinkingStatus = computeDrinkingReadiness(vintage);
        const w = vintage.drinking_window_current;
        groups.set(key, {
          key, wine, vintage, producer, cellar, appellation,
          format: bottle.format,
          qty: 1,
          bottles: [bottle],
          drinkingStatus,
          drinkingWindow: { from: w?.from_year ?? 0, to: w?.to_year ?? 0 },
          region: canonicalRegion(producer.region ?? ''),
          isSpecialOccasion: bottle.is_special_occasion,
          avgScore: null,
          marketPrice: null,
          avgCostEur: null,
        });
      }
    }
    return Array.from(groups.values());
  }, [bottles, vintageMap, wineMap, producersMap, cellarMap, appellationsMap]);

  // Step 1b: unique vintage IDs for bulk score/price fetches
  const vintageIds = useMemo(
    () => Array.from(new Set(allGrouped.map(g => g.vintage.id))),
    [allGrouped],
  );

  // Step 1c: bulk-fetch best 100pt critic score per vintage
  const { data: scoresMap = new Map<string, number>() } = useQuery({
    queryKey: ['critic_scores_bulk', vintageIds],
    queryFn: async (): Promise<Map<string, number>> => {
      if (vintageIds.length === 0) return new Map();
      // Firestore 'in' operator supports up to 30 items — chunk accordingly
      const chunks: string[][] = [];
      for (let i = 0; i < vintageIds.length; i += 30) chunks.push(vintageIds.slice(i, i + 30));
      const allScores: CriticScore[] = [];
      for (const chunk of chunks) {
        const snap = await getDocs(
          query(collection(db, 'critic_scores'), where('vintage_id', 'in', chunk)),
        );
        snap.docs.forEach(d => allScores.push({ id: d.id, ...d.data() } as CriticScore));
      }
      // Best 100pt score per vintage (highest wins)
      const map = new Map<string, number>();
      for (const score of allScores) {
        if (score.score_type !== '100_pt') continue;
        const prev = map.get(score.vintage_id);
        if (prev === undefined || score.score_value > prev) map.set(score.vintage_id, score.score_value);
      }
      return map;
    },
    enabled: vintageIds.length > 0,
    staleTime: 5 * 60_000,
  });

  // Step 1d: bulk-fetch latest market price per vintage
  const { data: pricesMap = new Map<string, MarketPriceHistory>() } = useQuery({
    queryKey: ['market_prices_bulk', vintageIds],
    queryFn: (): Promise<Map<string, MarketPriceHistory>> =>
      getLatestPricesForVintages(vintageIds),
    enabled: vintageIds.length > 0,
    staleTime: 5 * 60_000,
  });

  // Step 1e: enrich grouped rows with scores + prices + avg purchase cost
  const allGroupedEnriched = useMemo<GroupedInventoryRow[]>(() => {
    return allGrouped.map(g => {
      const costs = g.bottles.map(b => b.cost_eur_at_purchase).filter((c): c is number => typeof c === 'number' && c > 0);
      const avgCostEur = costs.length > 0 ? Math.round(costs.reduce((s, c) => s + c, 0) / costs.length) : null;
      return {
        ...g,
        avgScore:    scoresMap.get(g.vintage.id) ?? null,
        marketPrice: pricesMap.get(g.vintage.id)?.price ?? null,
        avgCostEur,
      };
    });
  }, [allGrouped, scoresMap, pricesMap]);

  // Step 2: compute facets from the full unfiltered set
  const facets = useMemo<FacetCounts>(() => {
    const byCellar         = new Map<string, number>();
    const byRegion         = new Map<string, number>();
    const byStyle          = new Map<WineStyle, number>();
    const byDrinkingStatus = new Map<DrinkingReadiness, number>();
    let specialOccasionCount = 0;
    let homeBottles = 0;

    for (const g of allGroupedEnriched) {
      byCellar.set(g.cellar.id, (byCellar.get(g.cellar.id) ?? 0) + g.qty);
      byRegion.set(g.region, (byRegion.get(g.region) ?? 0) + g.qty);
      byStyle.set(g.wine.style, (byStyle.get(g.wine.style) ?? 0) + g.qty);
      byDrinkingStatus.set(g.drinkingStatus, (byDrinkingStatus.get(g.drinkingStatus) ?? 0) + g.qty);
      if (g.isSpecialOccasion) specialOccasionCount += g.qty;
      if (homeCellarIds.has(g.cellar.id)) homeBottles += g.qty;
    }
    return {
      byCellar, byRegion, byStyle, byDrinkingStatus,
      specialOccasionCount,
      totalBottles: allGroupedEnriched.reduce((s, g) => s + g.qty, 0),
      homeBottles,
    };
  }, [allGroupedEnriched, homeCellarIds]);

  // Step 3: apply filters
  const grouped = useMemo<GroupedInventoryRow[]>(() => {
    let rows = allGroupedEnriched;

    if (filters.availableAtHome && filters.cellarIds.length === 0) {
      rows = rows.filter(g => homeCellarIds.has(g.cellar.id));
    }
    if (filters.cellarIds.length > 0) {
      const s = new Set(filters.cellarIds);
      rows = rows.filter(g => s.has(g.cellar.id));
    }
    if (filters.regions.length > 0) {
      const s = new Set(filters.regions);
      rows = rows.filter(g => s.has(g.region));
    }
    if (filters.styles.length > 0) {
      const s = new Set(filters.styles);
      rows = rows.filter(g => s.has(g.wine.style));
    }
    if (filters.style) {
      rows = rows.filter(g => g.wine.style === filters.style);
    }
    if (filters.drinkingStatuses.length > 0) {
      const s = new Set(filters.drinkingStatuses);
      rows = rows.filter(g => s.has(g.drinkingStatus));
    }
    if (filters.drinkingStatus) {
      rows = rows.filter(g => g.drinkingStatus === filters.drinkingStatus);
    }
    if (filters.vintageFrom != null) {
      rows = rows.filter(g => g.vintage.year >= (filters.vintageFrom ?? 0));
    }
    if (filters.vintageTo != null) {
      rows = rows.filter(g => g.vintage.year <= (filters.vintageTo ?? 9999));
    }
    if (filters.specialOccasion) {
      rows = rows.filter(g => g.isSpecialOccasion);
    }
    if (filters.search.trim()) {
      const q = foldSearch(filters.search.trim());
      rows = rows.filter(g =>
        foldSearch([g.producer.name, g.wine.cuvee_name ?? '', String(g.vintage.year), g.appellation?.name ?? ''].join(' '))
          .includes(q)
      );
    }

    switch (filters.sortBy) {
      case 'producer':
        rows = [...rows].sort((a, b) => a.producer.name.localeCompare(b.producer.name));
        break;
      case 'vintage_asc':
        rows = [...rows].sort((a, b) => a.vintage.year - b.vintage.year);
        break;
      case 'vintage_desc':
        rows = [...rows].sort((a, b) => b.vintage.year - a.vintage.year);
        break;
      case 'price_asc':
        rows = [...rows].sort((a, b) => {
          if (a.marketPrice == null && b.marketPrice == null) return 0;
          if (a.marketPrice == null) return 1;
          if (b.marketPrice == null) return -1;
          return a.marketPrice - b.marketPrice;
        });
        break;
      case 'price_desc':
        rows = [...rows].sort((a, b) => {
          if (a.marketPrice == null && b.marketPrice == null) return 0;
          if (a.marketPrice == null) return 1;
          if (b.marketPrice == null) return -1;
          return b.marketPrice - a.marketPrice;
        });
        break;
      case 'drinking_window':
      default: {
        const order: Record<DrinkingReadiness, number> = {
          peak: 0, declining: 1, developing: 2, too_young: 3, over_the_hill: 4,
        };
        rows = [...rows].sort((a, b) => {
          const d = order[a.drinkingStatus] - order[b.drinkingStatus];
          return d !== 0 ? d : a.producer.name.localeCompare(b.producer.name);
        });
        break;
      }
    }
    return rows;
  }, [allGroupedEnriched, filters, homeCellarIds]);

  const stats = useMemo(() => {
    // Value = market price first, cost fallback second, unpriced bottles
    // excluded from the sum (but still counted) — see utils/valuation.ts.
    let valuation = emptyValuationTotals();
    let costBasisEur = 0;
    for (const g of allGroupedEnriched) {
      for (const b of g.bottles) {
        valuation = accumulateValuation(valuation, g.marketPrice, b.cost_eur_at_purchase);
        costBasisEur += b.cost_eur_at_purchase ?? 0;
      }
    }
    return {
      total: facets.totalBottles,
      homeCount: facets.homeBottles,
      cellarCount: cellars.length,
      /** Market value w/ cost fallback — the figure to show as "portfolio value". */
      marketValueEur: valuation.valueEur,
      /** Raw purchase-price sum — separate, labeled metric. Never blend with marketValueEur. */
      costBasisEur,
      /** Bottles with neither a market price nor a recorded cost. */
      unpricedCount: valuation.unpricedCount,
      peakCount: facets.byDrinkingStatus.get('peak') ?? 0,
      developingCount: facets.byDrinkingStatus.get('developing') ?? 0,
    };
  }, [facets, allGroupedEnriched, cellars]);

  return { grouped, stats, facets, cellars, isLoading };
}

// ---------------------------------------------------------------------------
// useInventory - backward-compat individual bottle rows
// Used by ConsumedPage, DashboardPage, etc.
// ---------------------------------------------------------------------------

export function useInventory(filters: InventoryFilters = DEFAULT_FILTERS) {
  const { data: bottles = [], isLoading: loadingBottles } = useAllBottles('in_cellar');
  const { data: vintages = [], isLoading: loadingVintages } = useAllVintages();
  const { data: wines = [], isLoading: loadingWines } = useWines({});
  const producersMap = useProducersMap();
  const { data: cellars = [], isLoading: loadingCellars } = useCellars();

  const isLoading = loadingBottles || loadingVintages || loadingWines || loadingCellars;

  const vintageMap   = useMemo(() => new Map(vintages.map(v => [v.id, v])), [vintages]);
  const wineMap      = useMemo(() => new Map(wines.map(w => [w.id, w])),    [wines]);
  const cellarMap    = useMemo(() => new Map(cellars.map(c => [c.id, c])),  [cellars]);
  const homeCellarId = useMemo(() => cellars.find(c => c.is_home)?.id ?? null, [cellars]);

  const rows = useMemo<InventoryRow[]>(() => {
    const result: InventoryRow[] = [];
    for (const bottle of bottles) {
      const vintage = vintageMap.get(bottle.vintage_id);
      if (!vintage) continue;
      const wine = wineMap.get(vintage.wine_id);
      if (!wine) continue;
      const producer = producersMap.get(wine.producer_id);
      if (!producer) continue;
      const cellar = cellarMap.get(bottle.current_cellar_id);
      if (!cellar) continue;

      const drinkingStatus = computeDrinkingReadiness(vintage);

      if (filters.availableAtHome && cellar.id !== homeCellarId) continue;
      if (filters.style && wine.style !== filters.style) continue;
      if (filters.drinkingStatus && drinkingStatus !== filters.drinkingStatus) continue;
      if (filters.styles.length > 0 && !filters.styles.includes(wine.style)) continue;
      if (filters.drinkingStatuses.length > 0 && !filters.drinkingStatuses.includes(drinkingStatus)) continue;

      if (filters.search.trim()) {
        const q = foldSearch(filters.search.trim());
        const hay = foldSearch([producer.name, wine.cuvee_name ?? '', String(vintage.year)].join(' '));
        if (!hay.includes(q)) continue;
      }

      result.push({ bottle, vintage, wine, producer, cellar, drinkingStatus, marketPrice: null });
    }
    return result;
  }, [bottles, vintageMap, wineMap, producersMap, cellarMap, homeCellarId, filters]);

  // Bulk-fetch latest market price per vintage so stats (and any consumer
  // reading row.marketPrice) can price bottles market-first, cost-fallback.
  const vintageIds = useMemo(() => Array.from(new Set(rows.map(r => r.vintage.id))), [rows]);
  const { data: pricesMap = new Map<string, MarketPriceHistory>() } = useQuery({
    queryKey: ['market_prices_bulk', vintageIds],
    queryFn: (): Promise<Map<string, MarketPriceHistory>> => getLatestPricesForVintages(vintageIds),
    enabled: vintageIds.length > 0,
    staleTime: 5 * 60_000,
  });

  const rowsWithPrice = useMemo<InventoryRow[]>(
    () => rows.map(r => ({ ...r, marketPrice: pricesMap.get(r.vintage.id)?.price ?? null })),
    [rows, pricesMap],
  );

  const stats = useMemo(() => {
    let valuation = emptyValuationTotals();
    let costBasisEur = 0;
    for (const r of rowsWithPrice) {
      valuation = accumulateValuation(valuation, r.marketPrice, r.bottle.cost_eur_at_purchase);
      costBasisEur += r.bottle.cost_eur_at_purchase ?? 0;
    }
    return {
      total: rowsWithPrice.length,
      peakCount: rowsWithPrice.filter(r => r.drinkingStatus === 'peak').length,
      developingCount: rowsWithPrice.filter(r => r.drinkingStatus === 'developing').length,
      /** Market value w/ cost fallback — the figure to show as "value". */
      marketValueEur: valuation.valueEur,
      /** Raw purchase-price sum — separate, labeled metric. Never blend with marketValueEur. */
      costBasisEur,
      /** Bottles with neither a market price nor a recorded cost. */
      unpricedCount: valuation.unpricedCount,
    };
  }, [rowsWithPrice]);

  return { rows: rowsWithPrice, stats, isLoading };
}

// ---------------------------------------------------------------------------
// useConsumedInventory - for the Consumed archive page
// ---------------------------------------------------------------------------

export function useConsumedInventory() {
  const { data: bottles = [], isLoading: loadingBottles } = useAllBottles('consumed');
  const { data: vintages = [], isLoading: loadingVintages } = useAllVintages();
  const { data: wines = [], isLoading: loadingWines } = useWines({});
  const producersMap = useProducersMap();
  const { data: cellars = [], isLoading: loadingCellars } = useCellars();

  const isLoading = loadingBottles || loadingVintages || loadingWines || loadingCellars;

  const vintageMap = useMemo(() => new Map(vintages.map(v => [v.id, v])), [vintages]);
  const wineMap    = useMemo(() => new Map(wines.map(w => [w.id, w])),    [wines]);
  const cellarMap  = useMemo(() => new Map(cellars.map(c => [c.id, c])),  [cellars]);

  const rows = useMemo<InventoryRow[]>(() => {
    const result: InventoryRow[] = [];
    for (const bottle of bottles) {
      const vintage = vintageMap.get(bottle.vintage_id);
      if (!vintage) continue;
      const wine = wineMap.get(vintage.wine_id);
      if (!wine) continue;
      const producer = producersMap.get(wine.producer_id);
      if (!producer) continue;
      const cellar = cellarMap.get(bottle.current_cellar_id);
      if (!cellar) continue;
      result.push({ bottle, vintage, wine, producer, cellar, drinkingStatus: 'peak', marketPrice: null });
    }
    return result;
  }, [bottles, vintageMap, wineMap, producersMap, cellarMap]);

  return { rows, isLoading };
}
